import { DomainError } from "./errors";
import { formatDateBR, isIsoDate } from "./dates";
import { daysBetween } from "./finance";

// ===== Lotes (RF-SUP-07, T3.2; 05 §5 "Lote: saída sugere FEFO") =====

/** nLote da NF-e: 1 a 20 caracteres. */
export const MAX_LOT_CODE_LENGTH = 20;
/** Alerta de vencimento: lotes que vencem nos próximos 30 dias (padrão da tela e do sino). */
export const LOT_ALERT_DEFAULT_DAYS = 30;
/** Grupos `rastro` por item da NF-e (leiaute: 0–500). */
export const MAX_RASTRO_PER_ITEM = 500;

/** Código do lote: sem caracteres de controle, aparado; vazio = `lot_required`, longo = `invalid_lot`. */
export function normalizeLotCode(raw: string): string {
  // eslint-disable-next-line no-control-regex
  const code = raw.replace(/[\u0000-\u001f\u007f]/g, "").trim();
  if (!code) throw new DomainError("lot_required", "Informe o código do lote.");
  if (code.length > MAX_LOT_CODE_LENGTH)
    throw new DomainError("invalid_lot", `O código do lote tem até ${MAX_LOT_CODE_LENGTH} caracteres.`);
  return code;
}

/** Datas do lote (AAAA-MM-DD, opcionais): validade não anterior à fabricação. */
export function lotDatesProblem(manufacturedAt: string | null, expiresAt: string | null): string | null {
  if (manufacturedAt !== null && !isIsoDate(manufacturedAt)) return "Data de fabricação inválida.";
  if (expiresAt !== null && !isIsoDate(expiresAt)) return "Data de validade inválida.";
  if (manufacturedAt !== null && expiresAt !== null && expiresAt < manufacturedAt)
    return "A validade não pode ser anterior à fabricação.";
  return null;
}

/**
 * Lote lido do grupo `rastro` do XML do fornecedor (T3.2, revisão de segurança): o XML é de terceiro, então o lote é
 * limpo em vez de recusado — código sem caracteres de controle e com até 20 caracteres (vazio = descarta), data
 * impossível no calendário (30/02, ano 0) vira nula e validade antes da fabricação anula as duas. O resultado sempre
 * passa por `normalizeLotCode` e `lotDatesProblem`, então a entrada da nota nunca trava por causa do lote.
 */
export function cleanInboundLot(l: {
  code: string;
  manufacturedAt: string | null;
  expiresAt: string | null;
}): { code: string; manufacturedAt: string | null; expiresAt: string | null } | null {
  const code = l.code
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .trim()
    .slice(0, MAX_LOT_CODE_LENGTH)
    .trim();
  if (!code) return null;
  const date = (v: string | null) => (typeof v === "string" && isIsoDate(v) ? v : null);
  let manufacturedAt = date(l.manufacturedAt);
  let expiresAt = date(l.expiresAt);
  if (manufacturedAt && expiresAt && expiresAt < manufacturedAt) manufacturedAt = expiresAt = null;
  return { code, manufacturedAt, expiresAt };
}

export type LotStatus = "expired" | "expiring" | "valid" | "no_expiry";

/** Dias de `today` até a validade (negativo = vencido há tantos dias). */
export const daysUntil = (expiresAt: string, today: string): number => daysBetween(today, expiresAt);

/** Vencido (validade antes de hoje), vencendo (até `days` dias, inclusive hoje), válido ou sem validade. */
export function lotStatus(expiresAt: string | null, today: string, days: number): LotStatus {
  if (expiresAt === null) return "no_expiry";
  if (expiresAt < today) return "expired";
  return daysUntil(expiresAt, today) <= days ? "expiring" : "valid";
}

export type LotStock = { lotId: string; code: string; expiresAt: string | null; physicalQ4: number };
export type LotTake = { lotId: string; qtyQ4: number };

const byCode = (a: LotStock, b: LotStock) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0);

/**
 * Ordem FEFO (primeiro a vencer, primeiro a sair): lotes válidos pela validade (sem validade depois deles) e os
 * vencidos por último — a venda não pega o vencido enquanto houver outro. Empate pelo código.
 */
export function fefoOrder(lots: readonly LotStock[], today: string): LotStock[] {
  const rank = (l: LotStock) => (l.expiresAt === null ? 1 : l.expiresAt < today ? 2 : 0);
  return [...lots].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    if (a.expiresAt !== b.expiresAt && a.expiresAt !== null && b.expiresAt !== null)
      return a.expiresAt < b.expiresAt ? -1 : 1;
    return byCode(a, b);
  });
}

/** "Sem lote" = físico do produto no depósito − Σ saldos dos lotes (pode ser negativo). */
export const semLoteQ4 = (totalPhysicalQ4: number, lots: readonly LotStock[]): number =>
  totalPhysicalQ4 - lots.reduce((a, l) => a + l.physicalQ4, 0);

function assertTakeQty(q: number) {
  if (!Number.isSafeInteger(q) || q <= 0)
    throw new DomainError("invalid_qty", "Quantidade do lote inválida.");
}

/**
 * Lotes de uma saída de `qtyQ4` (o que não vai a lote sai do "Sem lote"):
 * - `explicit` (escolha manual): soma igual à quantidade (`lot_qty_mismatch`), sem repetir lote; com o negativo
 *   bloqueado cada lote precisa de saldo (`insufficient_lot_stock`).
 * - automática: `preferred` primeiro (plano da NF-e, lote de origem do estorno), até o saldo positivo de cada lote;
 *   depois o "Sem lote" positivo; depois FEFO. O que faltar fica no "Sem lote" (negativo liberado).
 */
export function allocateLotsOut(i: {
  qtyQ4: number;
  totalPhysicalQ4: number;
  lots: readonly LotStock[];
  today: string;
  allowNegative: boolean;
  explicit?: readonly LotTake[] | null;
  preferred?: readonly LotTake[] | null;
}): LotTake[] {
  const byId = new Map(i.lots.map((l) => [l.lotId, l]));
  if (i.explicit) {
    const seen = new Set<string>();
    let sum = 0;
    for (const t of i.explicit) {
      assertTakeQty(t.qtyQ4);
      if (seen.has(t.lotId)) throw new DomainError("validation", "Lote repetido na saída.");
      seen.add(t.lotId);
      sum += t.qtyQ4;
      const l = byId.get(t.lotId);
      if (!i.allowNegative && (l?.physicalQ4 ?? 0) < t.qtyQ4)
        throw new DomainError(
          "insufficient_lot_stock",
          l
            ? `O lote ${l.code} não tem saldo suficiente neste depósito.`
            : "O lote não tem saldo neste depósito.",
        );
    }
    if (sum !== i.qtyQ4)
      throw new DomainError("lot_qty_mismatch", "A soma das quantidades dos lotes difere da quantidade.");
    return i.explicit.map((t) => ({ lotId: t.lotId, qtyQ4: t.qtyQ4 }));
  }
  const taken = new Map<string, number>();
  let left = i.qtyQ4;
  const take = (l: LotStock | undefined, max: number) => {
    if (!l || left <= 0) return;
    const free = Math.max(0, l.physicalQ4 - (taken.get(l.lotId) ?? 0));
    const q = Math.min(free, max, left);
    if (q <= 0) return;
    taken.set(l.lotId, (taken.get(l.lotId) ?? 0) + q);
    left -= q;
  };
  for (const p of i.preferred ?? []) take(byId.get(p.lotId), p.qtyQ4);
  left -= Math.min(left, Math.max(0, semLoteQ4(i.totalPhysicalQ4, i.lots)));
  for (const l of fefoOrder(i.lots, i.today)) take(l, left);
  return [...taken.entries()].map(([lotId, qtyQ4]) => ({ lotId, qtyQ4 }));
}

/** qLote do `rastro`: quantidade com 3 casas decimais (q4 → milésimos, meio para cima). */
export function formatQLote(qtyQ4: number): string {
  const m = Math.floor((qtyQ4 + 5) / 10);
  return `${Math.floor(m / 1000)}.${String(m % 1000).padStart(3, "0")}`;
}

export type NfeLot = { code: string; qtyQ4: number; manufacturedAt: string | null; expiresAt: string | null };
export type NfeRastro = { nLote: string; qLote: number; dFab: string; dVal: string };

/**
 * Grupo `rastro` do item (NT 2016.002): nLote, qLote, dFab e dVal (as duas datas são obrigatórias). Lote sem uma das
 * datas, com quantidade que arredonda a zero ou além dos 500 grupos vai como texto ("Lote X (validade dd/mm/aaaa)")
 * para o `infAdProd`.
 */
export function rastroOf(lots: readonly NfeLot[]): { rastro: NfeRastro[]; note: string | null } {
  const rastro: NfeRastro[] = [];
  const notes: string[] = [];
  for (const l of lots) {
    const qLote = formatQLote(l.qtyQ4);
    if (l.manufacturedAt && l.expiresAt && qLote !== "0.000" && rastro.length < MAX_RASTRO_PER_ITEM)
      rastro.push({ nLote: l.code, qLote: Number(qLote), dFab: l.manufacturedAt, dVal: l.expiresAt });
    else notes.push(`Lote ${l.code}${l.expiresAt ? ` (validade ${formatDateBR(l.expiresAt)})` : ""}`);
  }
  return { rastro, note: notes.length ? notes.join("; ") : null };
}
